import React, { useState } from 'react';

// Dependency-free SVG charts for the Cash Flow forecast, matching this
// codebase's existing convention (see ExpenseCharts.js) of plain SVG rather
// than pulling in a charting library - recharts is listed in package.json
// but isn't actually used anywhere yet, and this sandbox has no `npm
// install` to verify it resolves (see CLAUDE.md section 6).

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
// Compact form for on-chart labels ("₹4.5L" instead of "₹4,50,000") so a
// direct value label doesn't overrun the bar/point it's attached to.
const moneyShort = (n) => {
  const v = Number(n || 0);
  const abs = Math.abs(v);
  if (abs >= 10000000) return `₹${(v / 10000000).toFixed(1)}Cr`;
  if (abs >= 100000) return `₹${(v / 100000).toFixed(1)}L`;
  if (abs >= 1000) return `₹${(v / 1000).toFixed(0)}K`;
  return money(v);
};

const W = 760;
const PAD_L = 68;
const PAD_R = 12;

// Small floating tooltip, positioned by percentage of the chart's own box
// so it tracks correctly regardless of how the SVG is scaled by the
// browser. `pos` is {leftPct, topPct} in the wrapping div's own coordinate
// space; pass null to hide.
const ChartTooltip = ({ pos, children }) => {
  if (!pos) return null;
  return (
    <div
      className="absolute z-10 pointer-events-none bg-gray-900 text-white text-xs rounded px-2.5 py-2 shadow-lg whitespace-nowrap"
      style={{ left: `${pos.leftPct}%`, top: `${pos.topPct}%`, transform: 'translate(-50%, -100%) translateY(-8px)' }}
    >
      {children}
    </div>
  );
};

// Rolling projected balance - line + area, with the safety buffer and any
// below-zero stretch called out visually. periods: [{week_label, balance,
// net}], in chronological order. The end point and the lowest point always
// carry a visible value label; every point shows exact figures on hover.
export const BalanceChart = ({ periods, bufferAmount = 0 }) => {
  const H = 220;
  const PAD_T = 26;
  const PAD_B = 26;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;
  const n = periods.length;
  const [hover, setHover] = useState(null);

  const balances = periods.map((p) => p.balance);
  const values = [...balances, 0, bufferAmount];
  let min = Math.min(...values);
  let max = Math.max(...values);
  let span = (max - min) || 1;
  min -= span * 0.1;
  max += span * 0.1;
  span = max - min;

  const x = (i) => (n <= 1 ? PAD_L + innerW / 2 : PAD_L + (innerW * i) / (n - 1));
  const y = (v) => PAD_T + innerH - ((v - min) / span) * innerH;

  const linePoints = balances.map((v, i) => `${x(i)},${y(v)}`).join(' L ');
  const areaPath = `M ${x(0)},${y(0)} L ${linePoints} L ${x(n - 1)},${y(0)} Z`;
  const gridValues = [0, 1, 2, 3].map((g) => min + (span * g) / 3);
  const tickStep = n > 8 ? Math.ceil(n / 6) : 1;

  const lowIdx = balances.indexOf(Math.min(...balances));
  const lastIdx = n - 1;
  const slot = n > 1 ? innerW / (n - 1) : innerW;
  const hitW = Math.max(slot, 8);

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" style={{ overflow: 'visible' }}
           onMouseLeave={() => setHover(null)}>
        {gridValues.map((gv, i) => (
          <g key={i}>
            <line x1={PAD_L} x2={W - PAD_R} y1={y(gv)} y2={y(gv)} stroke="#eef0f2" strokeWidth="1" />
            <text x={2} y={y(gv) + 3} fontSize="10" fill="#9ca3af">{moneyShort(gv)}</text>
          </g>
        ))}
        <line x1={PAD_L} x2={W - PAD_R} y1={y(0)} y2={y(0)} stroke="#c8ccd1" strokeWidth="1" />
        {bufferAmount > 0 && (
          <line x1={PAD_L} x2={W - PAD_R} y1={y(bufferAmount)} y2={y(bufferAmount)} stroke="#9ca3af" strokeWidth="1" strokeDasharray="3 3" />
        )}
        {min < 0 && (
          <rect x={PAD_L} y={PAD_T} width={innerW} height={y(0) - PAD_T} fill="#ef4444" opacity="0.06" />
        )}
        <path d={areaPath} fill="#2F6690" opacity="0.12" />
        <path d={`M ${linePoints}`} fill="none" stroke="#2F6690" strokeWidth="2" />
        {periods.map((p, i) => (
          i % tickStep === 0 && (
            <text key={i} x={x(i)} y={H - 6} fontSize="10" fill="#9ca3af" textAnchor="middle">{p.week_label}</text>
          )
        ))}

        {/* Always-visible labels: end point and the 13-week low, so the two
            numbers that matter most don't require a hover to see. */}
        <circle cx={x(lastIdx)} cy={y(balances[lastIdx])} r="4" fill="#fff" stroke="#2F6690" strokeWidth="2" />
        <text x={x(lastIdx)} y={y(balances[lastIdx]) - 10} fontSize="10.5" fontWeight="700" fill="#2F6690" textAnchor="end">
          {moneyShort(balances[lastIdx])}
        </text>
        {lowIdx !== lastIdx && (
          <>
            <circle cx={x(lowIdx)} cy={y(balances[lowIdx])} r="4" fill="#fff" stroke="#ef4444" strokeWidth="2" />
            <text x={x(lowIdx)} y={y(balances[lowIdx]) - 10} fontSize="10.5" fontWeight="700" fill="#ef4444" textAnchor="middle">
              {moneyShort(balances[lowIdx])}
            </text>
          </>
        )}

        {/* Hover crosshair + wide invisible hit strips, one per point */}
        {hover != null && (
          <line x1={x(hover)} x2={x(hover)} y1={PAD_T} y2={H - PAD_B} stroke="#9ca3af" strokeWidth="1" strokeDasharray="2 2" />
        )}
        {periods.map((p, i) => (
          <rect key={i} x={x(i) - hitW / 2} y={0} width={hitW} height={H} fill="transparent"
                onMouseEnter={() => setHover(i)} onMouseMove={() => setHover(i)} />
        ))}
        {hover != null && (
          <circle cx={x(hover)} cy={y(balances[hover])} r="4.5" fill="#2F6690" stroke="#fff" strokeWidth="1.5" />
        )}
      </svg>
      <ChartTooltip pos={hover != null ? { leftPct: (x(hover) / W) * 100, topPct: (y(balances[hover]) / H) * 100 } : null}>
        <div className="font-semibold mb-0.5">{periods[hover]?.week_label}</div>
        <div>Balance: <b>{money(periods[hover]?.balance)}</b></div>
        <div className="text-gray-300">Net this period: {periods[hover]?.net >= 0 ? '+' : ''}{money(periods[hover]?.net)}</div>
      </ChartTooltip>
    </div>
  );
};

// Categorical palette - inflow (advance called out from other receivables)
// and outflow broken down by source (materials/site/recurring/post-dated),
// so the composition of a day/week/month is visible at a glance, not just
// on hover. Fixed order, never cycled or reassigned per-filter.
const ADVANCE_COLOR = '#C9822E';
const OTHER_IN_COLOR = '#2F6690';
const MATERIALS_COLOR = '#DC2626';
const SITE_COLOR = '#F97316';
const RECURRING_COLOR = '#7C3AED';
const POST_DATED_COLOR = '#64748B';

const OUTFLOW_SEGMENTS = [
  { key: 'materials', label: 'Materials (Purchase)', color: MATERIALS_COLOR },
  { key: 'site', label: 'Site', color: SITE_COLOR },
  { key: 'recurring', label: 'Recurring expenses', color: RECURRING_COLOR },
  { key: 'post_dated', label: 'Post-dated cheques & dues', color: POST_DATED_COLOR },
];

export const NET_FLOW_LEGEND = [
  { label: 'Advance', color: ADVANCE_COLOR },
  { label: 'Other receivable', color: OTHER_IN_COLOR },
  ...OUTFLOW_SEGMENTS,
];

// Gross inflow (stacked: advance vs. everything else) above the line,
// gross outflow (stacked: materials/site/recurring/post-dated) below it -
// deliberately NOT net, so exactly which source is moving the needle on a
// given day/week is visible on the bar itself, not collapsed into one
// number. periods need inflow_breakdown and outflow_breakdown from the
// /forecast API. Value labels show directly on the chart when there's
// room (<=14 bars); denser views (daily) rely on the hover tooltip so
// labels don't collide - see references/marks-and-anatomy.md's "never a
// number on every point" rule this follows.
export const NetFlowChart = ({ periods }) => {
  const H = 190;
  const PAD_T = 10;
  const PAD_B = 26;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;
  const n = periods.length;
  const [hover, setHover] = useState(null);
  const showLabels = n <= 14;

  const maxAbs = Math.max(
    1,
    ...periods.map((p) => Math.max(p.inflow || 0, p.outflow || 0))
  ) * 1.15;
  const slot = innerW / n;
  const barW = Math.min(n <= 4 ? 48 : n <= 14 ? 22 : 10, slot * 0.55);
  const x = (i) => PAD_L + slot * i + slot / 2;
  const y0 = PAD_T + innerH / 2;
  // Inflow stacks upward from the zero line, outflow stacks downward -
  // two separate mappings, not one y() reused with a sign flip (that bug
  // shipped once already: calling y(cum) for a positive outflow total
  // moved the segment *up*, the same direction as inflow).
  const yUp = (v) => y0 - (v / maxAbs) * (innerH / 2);
  const yDown = (v) => y0 + (v / maxAbs) * (innerH / 2);
  const tickStep = n > 8 ? Math.ceil(n / 6) : 1;

  const hoverP = hover != null ? periods[hover] : null;

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" style={{ overflow: 'visible' }}
           onMouseLeave={() => setHover(null)}>
        <line x1={PAD_L} x2={W - PAD_R} y1={y0} y2={y0} stroke="#c8ccd1" strokeWidth="1" />
        <text x={2} y={y0 + 3} fontSize="10" fill="#9ca3af">₹0</text>

        {periods.map((p, i) => {
          const cx = x(i);
          const advance = p.inflow_breakdown?.advance || 0;
          const other = p.inflow_breakdown?.other || 0;
          const totalIn = advance + other;

          // Outflow stack, in OUTFLOW_SEGMENTS order, running from the
          // zero line downward.
          let cum = 0;
          const outRects = OUTFLOW_SEGMENTS.map((seg) => {
            const val = p.outflow_breakdown?.[seg.key] || 0;
            const segTop = yDown(cum);
            cum += val;
            const segBottom = yDown(cum);
            return { ...seg, val, top: segTop, height: Math.max(Math.abs(segBottom - segTop), val > 0 ? 1 : 0) };
          });
          const totalOut = cum; // == p.outflow, but summed from the same segments the bars are drawn from

          return (
            <g key={i}>
              {advance > 0 && (
                <rect x={cx - barW / 2} y={yUp(advance)} width={barW} height={Math.max(Math.abs(yUp(advance) - y0), 1)} rx="2" fill={ADVANCE_COLOR} />
              )}
              {other > 0 && (
                <rect x={cx - barW / 2} y={yUp(advance + other)} width={barW} height={Math.max(Math.abs(yUp(advance + other) - yUp(advance)), 1)} rx="2" fill={OTHER_IN_COLOR} />
              )}
              {outRects.map((seg) => seg.val > 0 && (
                <rect key={seg.key} x={cx - barW / 2} y={seg.top} width={barW} height={seg.height} rx="1.5" fill={seg.color} />
              ))}

              {showLabels && totalIn > 0 && (
                <text x={cx} y={yUp(totalIn) - 5} fontSize="9.5" fontWeight="600" fill={OTHER_IN_COLOR} textAnchor="middle">
                  {moneyShort(totalIn)}
                </text>
              )}
              {showLabels && totalOut > 0 && (
                <text x={cx} y={yDown(totalOut) + 12} fontSize="9.5" fontWeight="600" fill="#6b7280" textAnchor="middle">
                  {moneyShort(totalOut)}
                </text>
              )}
            </g>
          );
        })}

        {periods.map((p, i) => (
          i % tickStep === 0 && (
            <text key={i} x={x(i)} y={H - 6} fontSize="10" fill="#9ca3af" textAnchor="middle">{p.week_label}</text>
          )
        ))}

        {hover != null && (
          <line x1={x(hover)} x2={x(hover)} y1={PAD_T} y2={H - PAD_B} stroke="#9ca3af" strokeWidth="1" strokeDasharray="2 2" opacity="0.5" />
        )}
        {periods.map((p, i) => (
          <rect key={i} x={PAD_L + slot * i} y={0} width={slot} height={H} fill="transparent"
                onMouseEnter={() => setHover(i)} onMouseMove={() => setHover(i)} />
        ))}
      </svg>
      <ChartTooltip pos={hover != null ? { leftPct: (x(hover) / W) * 100, topPct: (PAD_T / H) * 100 } : null}>
        <div className="font-semibold mb-1">{hoverP?.week_label}</div>
        {(hoverP?.inflow_breakdown?.advance || 0) > 0 && (
          <div className="flex justify-between gap-3"><span style={{ color: ADVANCE_COLOR }}>Advance</span><b>{money(hoverP.inflow_breakdown.advance)}</b></div>
        )}
        {(hoverP?.inflow_breakdown?.other || 0) > 0 && (
          <div className="flex justify-between gap-3"><span style={{ color: OTHER_IN_COLOR }}>Other receivable</span><b>{money(hoverP.inflow_breakdown.other)}</b></div>
        )}
        {OUTFLOW_SEGMENTS.map((seg) => (hoverP?.outflow_breakdown?.[seg.key] || 0) > 0 && (
          <div key={seg.key} className="flex justify-between gap-3"><span style={{ color: seg.color }}>{seg.label}</span><b>{money(hoverP.outflow_breakdown[seg.key])}</b></div>
        ))}
        <div className="border-t border-gray-700 mt-1 pt-1 flex justify-between gap-3">
          <span className="text-gray-300">Net</span><b>{hoverP?.net >= 0 ? '+' : ''}{money(hoverP?.net)}</b>
        </div>
      </ChartTooltip>
    </div>
  );
};
